import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {AppDispatch, RootState} from "./store";
import {toast} from "react-toastify";
import User from "../models/User";
import {clearTokens, getAccessToken, setTokens} from "../services/tokenStore";
import axios from "axios";
import config from "../config/config";
import i18n from '../i18n';
import {clearAccountFrozen, isAccountFrozenError, markAccountFrozen} from "../services/accountFrozen";

interface UserState {
    isLoggedIn?: boolean;
    user: User | null;
    selectedWorkspaceId: string | null;
}

const initialUserState: UserState = {
    isLoggedIn: undefined,
    user: null,
    selectedWorkspaceId: null
}

const userSlice = createSlice({
    name: 'user',
    initialState: initialUserState,
    reducers: {
        checkLoginStatus: (state) => {
            const token = getAccessToken();
            state.isLoggedIn = !!token; // Updates the login status based on the cookie's presence
        },
        login: (state) => {
            state.isLoggedIn = true;
        },
        logout: (state) => {
            state.isLoggedIn = false;
            state.user = null;
            state.selectedWorkspaceId = null;
        },
        setUser: (state, action: PayloadAction<User>) => {
            state.user = action.payload;
            // Set default selected workspace if one exists and none is currently selected
            if (action.payload?.workspaces?.length > 0 && !state.selectedWorkspaceId) {
                state.selectedWorkspaceId = action.payload.workspaces[0].id;
            }
        },
        setSelectedWorkspaceId: (state, action: PayloadAction<string>) => {
            state.selectedWorkspaceId = action.payload;
        }
    }
});

export const {checkLoginStatus, setUser, setSelectedWorkspaceId} = userSlice.actions;
export const selectIsLoggedIn = (state: RootState) => state.user.isLoggedIn;
export const selectUser = (state: RootState) => state.user.user;
export const selectSelectedWorkspaceId = (state: RootState) => state.user.selectedWorkspaceId;
/**
 * Whether this user's team may see the creation history.
 *
 * ONE selector for all three entry points (route, mobile home row, desktop nav
 * button) so they cannot drift apart and leave a dead link. Coerced to a real
 * boolean: an absent flag is OFF, never "unknown" — the endpoints 403 when the
 * team is not enabled, so guessing optimistically would show a broken screen.
 */
export const selectGenerationHistoryEnabled = (state: RootState): boolean =>
    !!state.user.user?.team?.generationHistoryEnabled;

// Rest of your existing code remains the same
const api = axios.create({
    baseURL: config.apiUrl,
});

export const login = (email: string, password: string) => async (dispatch: AppDispatch, getState: () => RootState) => {

    try {
        const response = await api.post('/oauth/login', {
            email,
            password
        });

        if (response.data.accessToken && response.data.refreshToken) {
            // localStorage.removeItem('lang')
            setTokens({
                accessToken: response.data.accessToken,
                refreshToken: response.data.refreshToken,
            });

            clearAccountFrozen();
            dispatch(userSlice.actions.login())
        }

        (window as any)?.dataLayer?.push({
            'event': 'login',
            email
        });

        toast.success(i18n.t('login.notification.loginSuccess'));
        return true;
    } catch (error) {
        // 403 account_frozen comes only AFTER the password checked out, so it is
        // safe (and kinder) to say so instead of "check your credentials".
        if (isAccountFrozenError(error)) {
            markAccountFrozen();
            toast.error(i18n.t('login.notification.accountFrozen'));
        } else {
            toast.error(i18n.t('login.notification.loginFailed'));
        }
    }
    return false;

}

export const loginWithGoogleCredential = (credential: string) => async (dispatch: AppDispatch, getState: () => RootState) => {

    try {
        const response = await api.post('/oauth/google', {
            credential
        });

        if (response.data.accessToken && response.data.refreshToken) {
            setTokens({
                accessToken: response.data.accessToken,
                refreshToken: response.data.refreshToken,
            });

            clearAccountFrozen();
            dispatch(userSlice.actions.login())
        }

        (window as any)?.dataLayer?.push({
            'event': 'login',
            method: 'google'
        });

        toast.success(i18n.t('login.notification.loginSuccess'));
        return true;
    } catch (error) {
        if (isAccountFrozenError(error)) {
            markAccountFrozen();
            toast.error(i18n.t('login.notification.accountFrozen'));
            return false;
        }
        // Backend returns 403 when the Google email is not linked to any user.
        const status = axios.isAxiosError(error) ? error.response?.status : undefined;
        toast.error(i18n.t(status === 403
            ? 'login.notification.googleNoAccount'
            : 'login.notification.googleFailed'));
    }
    return false;

}

export const forgotPassword = (email: string) => async (dispatch: AppDispatch, getState: () => RootState) => {

    try {
        const response = await api.post('/oauth/forgot-password', {
            email,
        });

        toast.success('An email with password reset instructions was sent successfully');
        return true;
    } catch (error) {
        toast.error('Error, please try again later');
    }
    return false;

}


export const clearUserData = (dispatch: AppDispatch) => {
    dispatch(userSlice.actions.logout())

    clearTokens()
}

export default userSlice.reducer;